import {
  FlowTab,
  IFlow,
  ITabsDetails,
  IWork,
  Loader,
  PageRightDrawerHeader,
  PageRightDrawerHeaderClose,
  PageRightDrawerHeaderTitle,
  TPageRightDrawerProps,
  useDetailsPageLeftDrawerProps,
  useDrawerTabsStore,
  useIsOnline,
  DianaDataType,
} from '@diana/core';

import { Comments } from './Comments';
import { Navigation } from './DrawerTabs/Navigation/Navigation';
import { Stack } from '@mui/material';
import { WORK_PAGE_NS } from './i18n';
import { WorkAudits, Workflow } from './DrawerTabs';
import { WorkPageTabsEnum as WorkTabs } from './WorkPage.constants';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RelatedWork } from './DrawerTabs/RelatedWork/RelatedWork';

const DefaultTabs = [WorkTabs.Workflow, WorkTabs.Activities];

export function useWorkPageLeftDrawerProps({
  work,
  flow,
  isLoading,
  tabs = DefaultTabs,
  defaultTab = WorkTabs.Workflow,
}: {
  work?: IWork;
  flow?: null | IFlow;
  isLoading: boolean;
  tabs?: WorkTabs[];
  defaultTab?: WorkTabs;
}) {
  const { t } = useTranslation(WORK_PAGE_NS);

  const isOnline = useIsOnline();
  const configTabs = flow?.flowFeatures?.leftPanel?.tabs;
  const configDefaultTab = flow?.flowFeatures?.leftPanel?.defaultTab;

  const tabsToRender = useMemo(() => {
    if (!isOnline) return [WorkTabs.Workflow];
    return configTabs ? configTabs.map((t) => t.tab) : tabs;
  }, [configTabs, tabs, isOnline]);

  const tabsLabels = useMemo(
    () =>
      configTabs
        ? configTabs.reduce((m, t) => {
            // @ts-ignore
            m[t.tab] = t.label;
            return m;
          }, {})
        : {},
    [configTabs],
  );

  const resolvedDefaultTab = configDefaultTab
    ? configDefaultTab
    : configTabs
    ? tabsToRender[0]
    : defaultTab;

  const relatedWorkShowStatus = flow?.featureFlags?.relatedWorkShowStatus === true;

  const getContents = useCallback(
    (tab?: WorkTabs) => {
      switch (tab) {
        case WorkTabs.Navigation:
          return <Navigation flow={flow} />;
        case WorkTabs.Workflow:
          return (
            <Workflow flowId={flow?.id} work={work} steps={work?.steps} isLoading={isLoading} />
          );
        case WorkTabs.Activities:
          // Uncomment and create the "Audit" tab when the new API for activities is ready.
          // <WorkActivities workId={work?.id} flow={flow} isLoading={isLoading} />
          return <WorkAudits workId={work?.id} flow={flow} isLoading={isLoading} />;
        case WorkTabs.RelatedWork:
          if (!work) return null;
          return (
            <RelatedWork
              workId={work.id}
              relationships={work.relationships}
              showWorkStatus={relatedWorkShowStatus}
            />
          );
        default:
          return null;
      }
    },
    [flow, work, isLoading, relatedWorkShowStatus],
  );

  const tabItems: ITabsDetails<WorkTabs> = {
    contents: getContents,
    labels: useMemo(
      () =>
        tabsToRender
          .map((tab) => ({
            // @ts-ignore
            label: tabsLabels[tab] ?? t(`tabs.${tab}`),
            value: tab,
          }))
          .filter(({ value }) => tabsToRender.some((v) => v === value)),
      [t, tabsToRender, tabsLabels],
    ),
  };

  const activeLeftDrawerTab = useDrawerTabsStore.getState().activeTab;
  // if we are on the navigation tab and work is loaded, we don't want to show the loading state when user
  // switches work items in navigation tab, there will be a loading spinner rendered in the navigation tab
  const isLeftDrawerLoading =
    activeLeftDrawerTab === FlowTab.Navigation && !!work?.id ? false : isLoading;

  return useDetailsPageLeftDrawerProps<WorkTabs>({
    isLoading: isLeftDrawerLoading,
    tabs: tabItems,
    currentTab: resolvedDefaultTab,
  });
}

export function useWorkPageRightDrawerProps({
  work,
  isLoading,
}: {
  work?: IWork;
  isLoading: boolean;
}) {
  const { t } = useTranslation(WORK_PAGE_NS);

  const isEntity = work?.dianaDataType === DianaDataType.CompanyEntity;

  const content = useMemo(() => {
    if (isLoading || !work?.id) {
      return (
        <Stack
          justifyContent="center"
          alignItems="center"
          direction="column"
          sx={{ height: '50vh' }}
        >
          <Loader />
        </Stack>
      );
    }

    return work?.id && !isEntity ? <Comments workId={work.id} users={work.users} /> : null;
  }, [isLoading, work?.id, work?.users, isEntity]);

  return useMemo(
    (): TPageRightDrawerProps => ({
      isOpen: false,
      header: (
        <PageRightDrawerHeader icon={<PageRightDrawerHeaderClose />}>
          <PageRightDrawerHeaderTitle>{t('commentsDrawer.title')}</PageRightDrawerHeaderTitle>
        </PageRightDrawerHeader>
      ),
      content,
      persistent: true,
    }),
    [content, t],
  );
}
